/**
 * Headless site-production-board logic — stage grouping/labelling.
 * Platform-neutral: no DOM, no Radix, no router.
 *
 * Freshness classification and the now-ticker are shared with the
 * performance wallboard (`./wallboard`) rather than duplicated here —
 * both are unattended polling kiosk pages with the same "how stale is
 * too stale" question.
 */

import type {
  ProductionStage,
  SiteBoardJob,
} from "../data/site-production-board-types";

export const STAGE_ORDER: readonly ProductionStage[] = [
  "prepress",
  "printing",
  "postpress",
  "despatch",
];

export const STAGE_LABELS: Record<ProductionStage, string> = {
  prepress: "Prepress",
  printing: "Printing",
  postpress: "Postpress",
  despatch: "Despatch",
};

/**
 * Chart token per stage, read left-to-right as the job's journey: blue
 * (queued) → amber (running) → violet (finishing) → green (ready to go).
 * Reuses the existing categorical chart palette rather than minting new
 * colour tokens.
 *
 * Deliberately the raw `--chart-{n}` custom property, not the
 * `--color-chart-{n}` Tailwind `@theme inline` alias: that alias's
 * `var(--chart-n)` reference is only ever specified at `:root`, so its
 * *computed* value is resolved and frozen there and simply inherits
 * down already-resolved — it does not re-read `.dark`'s override on a
 * forced-dark subtree. The raw property is re-specified inside `.dark`
 * itself, so it flips correctly for any element within it.
 */
export const STAGE_CHART_TOKEN: Record<ProductionStage, string> = {
  prepress: "--chart-1",
  printing: "--chart-3",
  postpress: "--chart-4",
  despatch: "--chart-2",
};

/** Group board jobs into their stage columns, preserving input order within each. */
export function groupJobsByStage(
  jobs: readonly SiteBoardJob[],
): Record<ProductionStage, SiteBoardJob[]> {
  const grouped: Record<ProductionStage, SiteBoardJob[]> = {
    prepress: [],
    printing: [],
    postpress: [],
    despatch: [],
  };
  for (const job of jobs) {
    grouped[job.stage].push(job);
  }
  return grouped;
}

export type UrgencyTier = "overdue" | "due_today" | "future";

/**
 * Classify a due date against calendar days, not a rolling duration —
 * mirrors the red/amber/green rule on the real daily production list
 * every department already works from (confirmed against a real export:
 * red rows all had a due date before the list's date, amber rows were
 * all due that same day, green rows were all due later). A job due at
 * 08:00 today and one due at 23:00 today are both "due today", not one
 * urgent and one not.
 */
export function classifyUrgency(
  dueAtIso: string | null,
  nowMs: number,
): UrgencyTier {
  if (dueAtIso === null) return "future";
  const due = new Date(dueAtIso);
  const now = new Date(nowMs);
  const dueDay = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  if (dueDay < today) return "overdue";
  if (dueDay === today) return "due_today";
  return "future";
}

/**
 * Raw `--chart-n` / `--destructive` tokens per urgency tier — same
 * reasoning as `STAGE_CHART_TOKEN` on why the raw property, not the
 * `--color-*` alias. Reuses existing chart tokens rather than minting a
 * dedicated red/amber/green palette — chart-3 and chart-2 already read
 * as amber and green on this board's dark theme, and happen to land
 * close to the source list's own red/yellow/green.
 */
export const URGENCY_CHART_TOKEN: Record<UrgencyTier, string> = {
  overdue: "--destructive",
  due_today: "--chart-3",
  future: "--chart-2",
};
